import type { AnalyticConfig, UploadConfig } from '@mimir/common/dto/tenantModel';
import type { FolderUploadDialogRequest } from '@mimir/web/dialogs/folderUploadDialogRequest';
import type { ItemUploadRequestPayload } from '@mimir/web/dialogs/itemUploadDialogRequest';
import { showFolderUploadDialog } from '@mimir/web/dialogs/folderUpload';
import { getGlobalStore } from '@mimir/web/store';
import { getIconForSearchCategory, type ItemAnalyticProperties } from '@mimir/common/util/item';
import { itemTypeFromMediaType, mediaTypeFromFilename } from '@mimir/common/util/mediaContentType';
import { createUuid } from '@mimir/common/util/createUuid';
import { invariant } from '@mimir/common/util/invariant';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import type { ItemCreationRequestDto } from '@mimir/common/dto/itemCreationRequestDto';
import type { DynamoItemType } from '@mimir/common/dto/itemTypes';
import { ItemType } from '@mimir/common/dto/itemTypes';
import type { ItemMetaData } from '@mimir/common/dto/fieldValue';
import type { LayoutSettings, ViewType } from '@mimir/common/dto/mdfModels';
import { logErrorAndShowToast } from '@mimir/web/util/uiError';
import { axiosErrorStatusCode } from '@mimir/common/util/httpError';
import { deleteFolder, addItemToFolder } from '@mimir/web/api/globalClientFolder';
import { getItemFields } from '@mimir/web/util/api';
import { apiDeleteItem } from '@mimir/common/util/httpApi';
import type { ItemCreatedCallback} from '@mimir/client-common/upload/UploadItem';
import { UploadItem } from '@mimir/client-common/upload/UploadItem';
import type { PathItem } from '@mimir/primitive/pathItem';
import type { BasicUploadManager, ManagedUploadItem } from '@mimir/common/upload/BasicUploadManager';
import type { FileApi} from '@mimir/common/upload/fileApi';
import { isFileApi } from '@mimir/common/upload/fileApi';
import Axios from 'axios';
import type { FolderDto } from '@mimir/common/dto/folderModels';
import { combineAll } from '@mimir/common/async/combineAll';
import { showToast, TOAST } from '@mimir/web/eventBus';
import { TaskUrgency } from '@mimir/common/async/taskPriority';
import { makePriority } from '@mimir/common/async/prioritySemaphore';
import { MimirUploadGui } from './uploadGui';
import { constructS3CloneUploadPath, constructUploadFolderPath } from '@mimir/client-common/util/uploadPaths';
import { isDefaultUploadS3Clone } from '@mimir/client-common/upload/uploadUtils';
import { globalBackendClient } from '@mimir/client-common/globals/backendClient';
import Vue from 'vue';
import { DEFAULT_MDF_ID, SystemDefaultField } from '@mimir/common/mdf/mdfDefaults';
import type { DeduplicationResultDto, ItemDeduplicationKey } from '@mimir/common/dto/deduplicationDtos';
import type { TranscriptionService } from '@mimir/common/dto/asyncOperationItemCreationTypes';
import type { PersonDetectionParams } from '@mimir/common/dto/asyncOperationPrimitives';
import type { ItemTranscriptionSettings } from '@mimir/common/dto/asyncOperationModels';
import type { TextDetectionSettings } from '@mimir/common/dto/textDetectionSettings';
import { getAnalyticConfig } from '../util/analytics';
import type { SearchResultHit, LoadedItem } from '@mimir/common/dto/searchResultHit';
import { getExtensionFromFileName } from './getExtensionFromFileName';
import { ApiActions } from '@mimir/client-common/util/apiActions';
import { OperationApiContext } from '@mimir/common/util/operationApiContext';
import { ExponentialBackoff, isRetryable } from '@mimir/common/async/resilience';
import { getFolderFromCache, addFolderToParent, doExpandFolder, folders } from '../globals/folders';
import { showPleaseWaitDialog, hidePleaseWaitDialog } from '@mimir/web/dialogs/pleaseWait';
import { showItemUploadDialog } from '../dialogs/itemUpload';
import type { DroppedEntity, DroppedFile, DroppedFolder, DroppedRoot, ExtensionInfo } from './upload-types';
import type { ApplicationContext } from '@mimir/common/context/applicationContext';
import type { CompleteType } from '@mimir/common/util/general';
import { getMainAndSubtypeSchemas } from '@mimir/common/mdf/subtype';
import { storeDispatchDoUpload, storeDispatchUploadItemFiles } from '@mimir/web/store/storeDispatch';
import type { UploadItemFilesPayload } from '../store/storePayloads';
import { searchState } from '../globals/searchState';
import { mdfState } from '../globals/mdfState';
import { ensurePathForLocalCopy } from '@mimir/web/desktop/ensurePathForLocalCopy';

// Unfortunately there doesn't seem to be type information for these out of the box
// so this is a best effort to create it ourselves.
type WebKitEntry = {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  file: (successCallback: (file: File) => void, errorCallback?: (fileError: unknown) => void) => undefined;
  createReader: () => ItemReader,
};

type ItemReader = {
  readEntries: (successCallback: (entries: WebKitEntry[]) => void, errorCallback: (fileError: unknown) => void) => undefined;
};

export interface FolderUploadParams {
  imageConfig: Partial<AnalyticConfig>;
  videoConfig: AnalyticConfig;
  audioConfig: Partial<AnalyticConfig>;
  createRootItem: boolean;
  preserveFolderStructure: boolean;
  targetUploadLocationId: string;
  commonOrSingleMetadata?: ItemMetaData;
  videoMetadata?: ItemMetaData;
  audioMetadata?: ItemMetaData;
  imageMetadata?: ItemMetaData;
  fileMetadata?: ItemMetaData;
  relatedItemIds?: string[];
  createdCallbacks?: ItemCreatedCallback[];
  deduplicationDto?: DeduplicationResultDto;
}

export const checkIfItHasFolders = (items: DataTransferItemList) => {
  for (let i = 0; i < items.length; i++) {
    const item = items[i].webkitGetAsEntry() as unknown as WebKitEntry;

    if (item === null) {
      continue;
    }

    if (item.isDirectory) {
      return true;
    }
  }
  return false;
};

/**
 * Inspect a DataTransferItemList and generate a summary tree, inferring extra information
 * that UI components can easily display. We also store any file references we can then pick up
 * to do actual upload without needing to traverse the original list again.
 *
 * @param items DataTransferItemList from a drop event
 */
export const createSummaryTree = async (items: DataTransferItemList) => {

  const treeRoot: DroppedRoot = {
    type: 'root',
    name: 'Root',
    P2Detected: false,
    rootFolderP2Detected: false,
    P2AVClipDetected: false,
    itemType: ItemType.FOLDER,
    subItems: [],
    totalSize: 0,
    totalItems: 0,
    extensions: Vue.observable({}),
    folderCount: 0
  };

  await Promise.all([...items]
    .map(async (it) => {
      const entry = it.webkitGetAsEntry();
      if (entry) {
        await generateSummary(entry as unknown as WebKitEntry, treeRoot, treeRoot);
      }
    }));
  return treeRoot;
};

/**
 * Generate a summary for the given item. If it is a folder, it will call itself again to recursively traverse.
 * @param item The webkit entry from an DataTransferItemList
 * @param folderNode the folder this item is in
 * @param folderRoot the root folder
 * @param path a pointer to the current path
 */
const generateSummary = async (item: WebKitEntry, folderNode: DroppedFolder, folderRoot: DroppedRoot, path?: string): Promise<void> => {
  path = path || '';

  // For mac: do not ever upload these.
  if (item.name.startsWith('.DS_Store')) {
    return;
  }
  // Files whose names begin with ._ are AppleDouble files that we don't want to upload
  if (item.name.startsWith('._')) {
    return;
  }

  if (item.isFile) {
    const file = await getFile(item);
    const mediaType = file.type && !isEmpty(file.type) && !file.type.toLowerCase().includes('octet-stream') ? file.type : mediaTypeFromFilename(file.name);
    const itemType = itemTypeFromMediaType(mediaType);
    const extension = getExtensionFromFileName(file.name).toLowerCase();
    const f: DroppedFile = {
        type: 'file',
        name: file.name,
        file: file,
        itemType: itemType,
        size: file.size,
        extension
    };
    folderNode.subItems.push(f);

    // Cache data in root node.
    folderRoot.totalItems += 1;
    folderRoot.totalSize += file.size;
    if (folderRoot.extensions[f.extension]) {
      folderRoot.extensions[f.extension].count += 1;
      folderRoot.extensions[f.extension].size += file.size;
    } else {
      const ext = Vue.observable({
          itemType,
          extension: f.extension,
          count: 1,
          size: file.size,
          icon: getIconForSearchCategory(itemType),
          include: true,
      });
      Vue.set(folderRoot.extensions, f.extension, ext);
    }
  } else if (item.isDirectory) {
    const folder: DroppedFolder = {
      type: 'folder',
      itemType: ItemType.FOLDER,
      name: item.name,
      subItems: []
    };

    folderNode.subItems.push(folder);
    folderRoot.folderCount++;

    const isRoot = folderNode === folderRoot;
    const allEntries: WebKitEntry[] = await readAllDirectoryEntries(item.createReader());
    const p2ParseResult = await filterIfP2Detected(item, allEntries);
    if (p2ParseResult !== null) {
      folderRoot.P2Detected = true;
      folderRoot.P2AVClipDetected = p2ParseResult.P2AVClipDetected;
      folderRoot.rootFolderP2Detected = folderRoot.rootFolderP2Detected || isRoot; // Only update when not already set to true
    }

    let toProcess: WebKitEntry[] = [];
    // In the P2 AVClip use case we only want the files in the clip folder.
    if (p2ParseResult?.p2Folders !== undefined) {
      toProcess = p2ParseResult?.p2Folders;
    } else {
      toProcess = allEntries;
    }

    await Promise.all(toProcess.map(entry =>
      generateSummary(entry, folder, folderRoot, path + item.name + '/')));
  }
};


type P2ParseResult = {
  P2AVClipDetected: boolean,
  p2Folders?: WebKitEntry[],
} | null;

/**
 * Returns a filtered set of entries if P2 folder structure is detected, null otherwise.
 *
 * @param entries The entries of a 'CONTENTS' folder
 */
async function filterIfP2Detected(parent: WebKitEntry, children: WebKitEntry[]): Promise<P2ParseResult> {

  if (parent.name !== 'CONTENTS') {
    return null;
  }

  let audioFolder: WebKitEntry | undefined;
  let videoFolder: WebKitEntry | undefined;
  let clipFolder: WebKitEntry | undefined;
  let avClipFolder: WebKitEntry | undefined;

  const subFolders = children.filter(c => c.isDirectory);

  for (const entry of subFolders) {
    if (entry.name === 'AUDIO') {
      audioFolder = entry;
    } else if (entry.name === 'VIDEO') {
      videoFolder = entry;
    } else if (entry.name === 'CLIP') {
      clipFolder = entry;
    } else if (entry.name === 'AVCLIP') {
      avClipFolder = entry;
    }
  }

  if (avClipFolder !== undefined && clipFolder !== undefined) {
    // We found a AVCLIP folder. If we find files in this folder, we assume it
    // is of type P2 AVC-Long for now. Let's do a look ahead to see if there's
    // content in this folder as wWe only want to report AVCLIP and CLIP if
    // there actually is something in 'AVCLIP', to avoid false positives for old
    // P2 format cards that may have an empty AVCLIP folder

    const AVClipEntries: WebKitEntry[] = await readAllDirectoryEntries(avClipFolder.createReader());
    if (AVClipEntries.filter(entry => entry.isFile).length > 0) {
      return {
        P2AVClipDetected: true,
        p2Folders: [avClipFolder, clipFolder],
      };
    }
  }

  // If it doesn't look like we have a P2 structure using AVCLIP,
  // let's use the old style P2 if and only if we have VIDEO, AUDIO and CLIP
  if (audioFolder !== undefined && videoFolder !== undefined && clipFolder !== undefined) {
    return {
      P2AVClipDetected: false,
      p2Folders: [audioFolder, videoFolder, clipFolder],
    };
  }

  return {
    P2AVClipDetected: false
  };
}

// Get all the entries (files or sub-directories) in a directory
// by calling readEntries until it returns empty array. Browsers have limitations on
// how many entries are returned at a time (eg chrome returns max 100)
const readAllDirectoryEntries = async (directoryReader: ItemReader) => {
  const entries = [];
  let readEntries = await readEntriesPromise(directoryReader);
  while (readEntries.length > 0) {
    entries.push(...readEntries);
    readEntries = await readEntriesPromise(directoryReader);
  }
  return entries;
};

// Wrap readEntries in a promise to make working with readEntries easier
const readEntriesPromise = async (directoryReader: ItemReader): Promise<any> => {
  try {
    return new Promise((resolve, reject) => {
      directoryReader.readEntries(resolve, reject);
    });
  } catch (err) {
    console.log(err);
  }
};

export const isFolderNode = (entity: DroppedEntity): entity is DroppedFolder => {
  return entity.type === 'folder';
};

export const isFileNode = (entity: DroppedEntity): entity is DroppedFile => {
  return entity.type === 'file';
};

export const initP2Stitch = async (
  isProxyMode: boolean,
  applicationContext: ApplicationContext,
  tree: DroppedRoot,
  payload: FolderUploadParams,
  uploadManager: BasicUploadManager,
) => {
  showPleaseWaitDialog('Please wait, preparing..');

  try {
    // Set a unique id on the root folder that will be hidden to avoid name clashes.
    // There should only be one, and one folder, but defensively will loop properly
    for (const f of tree.subItems) {
      if (f.type === 'folder') {
        f.name = createUuid();
      }
    }

    const apiActions = getApiActionsWithBackoff();
    const rootFolder = await createFoldersFromTree(apiActions, tree, true);
    invariant(rootFolder?.id, 'P2 cards must have root folder');
    const rootFolderId = rootFolder.id;

    const items: (UploadItem & { file: FileApi })[] = [];
    const uploadToS3Clone_ = await uploadToS3Clone();
    const store = getGlobalStore();
    createUploadItems(items, [], tree, payload, tree.P2Detected, uploadToS3Clone_, tree.extensions, undefined, true);

    // Let's sort the items by file name so that the different files that compose
    // a clip are uploaded one after the other so that the backend can start remux
    // clips as soon as possible
    items.sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''));

    const itemIds = await createAllItems(items);
    const rootItemId = await createRootItemPlaceholder(payload, itemIds, tree.createdId!);

    store.commit('setUploadSummary', null, { root: true });

    /*
      Ignoring errors thrown by uploadItemFiles, since we have other means of
      conveying upload progress and errors.
    */
    completeP2StitchUpload(isProxyMode, applicationContext, rootItemId, items, rootFolderId, uploadManager).catch(noop).catch(ex => console.error(ex));
  } catch (error: unknown) {
    if (axiosErrorStatusCode(error) === 409) {
      logErrorAndShowToast('Detected a folder that already has the same name', error);
    } else {
      logErrorAndShowToast('An error has occurred', error);
    }
  } finally {
    hidePleaseWaitDialog();
  }
};


async function completeP2StitchUpload(
  isProxyMode: boolean,
  applicationContext: ApplicationContext,
  itemId: string,
  fileItems: ManagedUploadItem[],
  rootFolderId: string,
  uploadManager: BasicUploadManager,
) {
  const uploadPayload: UploadItemFilesPayload = {
    uploadManager,
    applicationContext,
    itemId,
    fileItems,
    isProxyMode,
  };

  try {
    storeDispatchUploadItemFiles(uploadPayload);
  } catch (ex) {
    if (Axios.isCancel(ex)) {

      /**
       * We want to reflect the delete operation immediately in the UI. Since a P2 root item is not retryable,
       * it makes no sense to keep it around in the search, especially since the item is also deleted permanently
       * in the deleteItem() call below. We notify of successful cancel through a regular toast message, as we can
       * assume the user is looking at the screen when canceling an upload.
       */
      searchState.ops.removeFromSearchById(itemId);
      showToast('Upload canceled', TOAST.INFO);
      await combineAll([
        apiDeleteItem(globalBackendClient, itemId, true, false, undefined),
        deleteFolder(rootFolderId),
      ]);
    }

    /*
      Ignoring errors thrown by uploadItemFiles, since we have other means of
      conveying upload progress and errors.
    */
  }
}


/**
 * When we detect a duplicate item, we process it as a copy
 * operation into a folder rather than an actual upload.
 */
interface DuplicateItemData {
  folderId: string;
  itemId: string;
}


/**
 * Execute folder upload - will route the user depending on use-case.
 */
export const startFolderUpload = async (
  isProxyMode: boolean,
  applicationContext: ApplicationContext,
  tree: DroppedRoot,
  payload: FolderUploadParams,
  uploadManager: BasicUploadManager,
) => {

  let rootFolderId: string | undefined = undefined;
  const store = getGlobalStore();
  try {
    // First create the folders needed, before we can process the upload items, as we need the folder id's
    // when we create the items already available. This may take some time, so let's make the user wait for it.
    if (payload.preserveFolderStructure) {
      showPleaseWaitDialog('Please wait, creating folder tree..');
    }
    const result = await doGenerateFolderStructure(tree, payload.preserveFolderStructure);

    // Bail out on upload if anything went wrong in folder creation.
    if (!result.succeeded) return;

    if (!result.preserveFolderStructure) {
      rootFolderId = result.rootFolder?.id;
    }

    // If cached, it means the folder was already created by a websocket event.
    const cachedFolder = getFolderFromCache(result.rootFolder?.id ?? '');
    if (!cachedFolder) {
      addFolderToParent(tree.createdId!, result.rootFolder as FolderDto);
    }
    if (folders.currentFolder?.id === tree.createdId) {
      // TODO: While migrating from search Vuex module to global object is was revealed that
      // we are breaking type safety here.
      searchState.ops.injectFolderIntoSearchResult(result.rootFolder as unknown as SearchResultHit);
    }
    doExpandFolder(tree.createdId!, store.getters.isFolderTreeActive);
  } finally {
    hidePleaseWaitDialog();
  }

  // Then we create all the upload items - important that its in this order because we need the folder id's
  const items: (UploadItem & { file: FileApi })[] = [];
  const duplicateItems: DuplicateItemData[] = [];
  const uploadToS3Clone_ = await uploadToS3Clone();
  createUploadItems(items, duplicateItems, tree, payload, tree.P2Detected, uploadToS3Clone_, tree.extensions, undefined, false, rootFolderId);

  if (duplicateItems.length > 0) {
    const promises = duplicateItems.map(d => addItemToFolder(d.folderId, d.itemId));
    await Promise.all(promises);
  }

  // Let's sort the items by file name so that the different files that compose
  // a clip are uploaded one after the other so that the backend can start remux
  // clips as soon as possible
  items.sort((a,b) => (a.title ?? '').localeCompare(b.title ?? ''));
  // No inject - user will be redirected.
  storeDispatchDoUpload({
    uploadManager,
    applicationContext,
    items,
    inject: false,
    isProxyMode,
  }).catch(noop);
};

function getApiActionsWithBackoff() {
  const backoff =  new ExponentialBackoff({
    timeLimit: 2 * 60 * 1000, // 2 minutes
    isRetryable: (err: unknown) => isRetryable(
      err,
      [] // Passing empty array to allow retrying 'POST' requests
    )
  });
  return new ApiActions(new OperationApiContext(globalBackendClient, backoff));
}

const doGenerateFolderStructure = async (tree: DroppedRoot, generateAll = true) => {
  try {
    const apiActions = getApiActionsWithBackoff();
    if (!generateAll) {
      invariant(tree.createdId, 'Expected existing parent id');
      const rootFolder = tree.subItems[0];
      invariant(tree.subItems.length === 1 && isFolderNode(rootFolder), 'Expected root folder only');
      const createdRootFolder = await apiActions.createFolder(tree.subItems[0].name, tree.createdId, false);
      rootFolder.createdId = createdRootFolder.id;

      return {
        succeeded: true,
        rootFolder: createdRootFolder,
      };
    } else {
      const folder = await createFoldersFromTree(apiActions, tree, false);
      return {
        succeeded: true,
        rootFolder: folder,
        preserveFolderStructure: true,
      };
    }
  } catch (error: unknown) {
    if (axiosErrorStatusCode(error) === 409) {
      logErrorAndShowToast('Detected a folder that already has the same name', error);
    } else {
      logErrorAndShowToast('An error has occurred', error);
    }

    return { succeeded: false };
  }
};

/**
 * Recursively traverse the tree and create a folder for each folder in the tree.
 * @param treeNode The root node to start from
 * @param isP2Stitch - P2stitch will mark folders as hidden, as well as exclude CLIP folder.
 */
const createFoldersFromTree = async (apiActions: ApiActions, treeNode: DroppedFolder, isP2Stitch = false): Promise<FolderDto | null> => {
  if (!treeNode.createdId) {
    return null;
  }

  let result: FolderDto | null = null;

  for (const droppedEntity of treeNode.subItems) {
    if (isFolderNode(droppedEntity)) {
      if (droppedEntity.name === 'CLIP' && isP2Stitch) continue;
      const folder = await apiActions.createFolder(droppedEntity.name, treeNode.createdId, isP2Stitch);
      result = folder;
      droppedEntity.createdId = folder.id;

      const subTree = await createFoldersFromTree(apiActions, droppedEntity, isP2Stitch);
      folder.hasSubfolder = subTree !== null;
    }
  }

  return result;
};

const createRootItemPlaceholder = async (
  params: FolderUploadParams,
  itemIds: string[],
  targetFolderId: string,
): Promise<string> => {
  invariant(params.commonOrSingleMetadata, 'Placeholders must contain metadata');

  const placeholder: Partial<ItemCreationRequestDto> = {
    metadata: params.commonOrSingleMetadata,
    itemType: ItemType.VIDEO,
    folderParents: [targetFolderId],
    parentItemIds: itemIds,
    // Let's set a progress info to let the search UI know that it
    // should poll this item for progress. Note that this status
    // will be displayed after upload is complete
    progressInfo: {
      status: 'Processing...',
      percentage: 0
    }
  };

  UploadItem.prepareItemConfigProps(placeholder, params.videoConfig);
  const searchHit: SearchResultHit = await searchState.ops.createPlaceHolderAndSearchHit({
    item: placeholder,
    inject: true,
    currentFolder: folders.currentFolder,
  });
  return searchHit.id;
};

const createAllItems = async (items: UploadItem[]): Promise<string[]> => {
  const promises = [];
  for (const item of items) {
    promises.push(item.beforeUpload(makePriority({ urgency: TaskUrgency.Modal })));
  }
  await Promise.all(promises);
  return items.map(i => i.id);
};


export interface NameAndSize {
  name: string;
  size: number;
}

export function tryFindItemId(file: NameAndSize, dedupDto: DeduplicationResultDto | undefined): string | undefined {
  if (!dedupDto) {
    return undefined;
  }

  for (const item of dedupDto.items) {
    if (item.name === file.name && item.size === file.size) {
      return item.id;
    }
  }

  return undefined;
}


/**
 * Recursively traverse the tree and create an upload item for each file found in the tree.
 *
 * @param uploadItems The array to fill with upload items
 * @param treeNode The root node to start from
 * @param dispatch Store dispatch for convenient callbacks
 * @param parentNodes parent nodes to construct folder paths for
 * @param rootItemId If present, created upload items will be marked as hidden and no additional config will be applied
 * @param rootFolderId If present, all created upload items will be placed in this folder
 */
function createUploadItems(
  uploadItems: (UploadItem & { file: FileApi })[],
  duplicateItems: DuplicateItemData[],
  treeNode: DroppedFolder,
  uploadParams: FolderUploadParams,
  p2Detected: boolean,
  willUploadToS3Clone: boolean,
  extensions: Record<string, ExtensionInfo>,
  parentNodes?: DroppedFolder[],
  isP2Stitch?: boolean,
  rootFolderId?: string,
) {
  if (!rootFolderId && !treeNode.createdId) {
    return;
  }

  for (const droppedEntity of treeNode.subItems) {
    if (isFileNode(droppedEntity)) {
      const existingItemId = tryFindItemId(droppedEntity, uploadParams.deduplicationDto);
      const folderId = rootFolderId || treeNode.createdId;
      if (existingItemId && folderId) {
        console.log(`File ${droppedEntity.name} already exists as item ${existingItemId}, putting it in folder ${folderId}`);
        duplicateItems.push({
          folderId,
          itemId: existingItemId,
        });
        continue;
      }

      if (!extensions[droppedEntity.extension].include) continue;
      const item: UploadItem = new UploadItem(new MimirUploadGui(), ensurePathForLocalCopy(droppedEntity.file), willUploadToS3Clone);

      if (item.derivedItemType === ItemType.AUDIO) {
        item.metadata = uploadParams.audioMetadata !== undefined ? cloneDeep(uploadParams.audioMetadata) : cloneDeep(uploadParams.commonOrSingleMetadata) ?? null;
      } else if (item.derivedItemType === ItemType.IMAGE) {
        item.metadata = uploadParams.imageMetadata !== undefined  ? cloneDeep(uploadParams.imageMetadata) : cloneDeep(uploadParams.commonOrSingleMetadata) ?? null;
      } else if (item.derivedItemType === ItemType.VIDEO) {
        item.metadata = uploadParams.videoMetadata !== undefined  ? cloneDeep(uploadParams.videoMetadata) : cloneDeep(uploadParams.commonOrSingleMetadata) ?? null;
      } else if (item.derivedItemType === ItemType.FILE) {
        item.metadata = uploadParams.fileMetadata !== undefined  ? cloneDeep(uploadParams.fileMetadata) : cloneDeep(uploadParams.commonOrSingleMetadata) ?? null;
      } else {
        item.metadata = cloneDeep(uploadParams.commonOrSingleMetadata) ?? null;
      }

      if (item.metadata) {
        item.metadata.formData[SystemDefaultField.Title] = droppedEntity.file.name;
      }

      invariant(isFileApi(item.file), 'Created item must have file');
      const newItem = item as typeof item & { file: FileApi };
      if (p2Detected && isP2Stitch === true) {
        newItem.isP2Part = true;
      }
      // For P2 stitch we want to create the items but mark them hidden from the user, except
      if (isP2Stitch === true) {
        newItem.shouldBeHidden = true;
      }

      const config: Partial<UploadConfig> = {
        folderId: rootFolderId || treeNode.createdId,
        targetUploadLocationId: uploadParams.targetUploadLocationId
      };

      if (!isP2Stitch && newItem.derivedItemType === ItemType.IMAGE) {
        newItem.setConfig({
          ...config,
          ...uploadParams.imageConfig
        });
      } else if (!isP2Stitch && newItem.derivedItemType === ItemType.VIDEO) {
        newItem.setConfig({
          ...config,
          ...uploadParams.videoConfig
        });
      } else if (!isP2Stitch && newItem.derivedItemType === ItemType.AUDIO) {
        newItem.setConfig({
          ...config,
          ...uploadParams.audioConfig
        });
      } else {
        newItem.setConfig(config);
      }

      if (parentNodes) {
        if (newItem.uploadToS3Clone) {
          const pathItems = parentNodes.map<PathItem>((p) => ({ name: p.name, folderId: p.createdId! }));
          newItem.folderPath = constructS3CloneUploadPath(pathItems);
        } else {
          newItem.folderPath = constructUploadFolderPath(parentNodes, newItem.folderPath);
        }
      }

      if (uploadParams.relatedItemIds) newItem.relatedItemIds = uploadParams.relatedItemIds;
      if (uploadParams.createdCallbacks?.length) uploadParams.createdCallbacks.forEach(cb => newItem.addCreatedItemCallback(cb));

      uploadItems.push(newItem);
    } else if (isFolderNode(droppedEntity)) {
      if (isP2Stitch && droppedEntity.name === 'CLIP') continue;
      const parents = parentNodes ? [...parentNodes, droppedEntity] : [droppedEntity];
      createUploadItems(uploadItems, duplicateItems, droppedEntity, uploadParams, p2Detected, willUploadToS3Clone, extensions, parents, isP2Stitch, rootFolderId);
    }
  }
}


/**
 * Returns true if the system is currently configured to upload to a S3 Gateway.
 */
export function uploadToS3Clone(): Promise<boolean> {
  return isDefaultUploadS3Clone(globalBackendClient);
}

export const getAnalyticConfigForItem = async (itemId: string) => {
  const analyticData =  await getItemFields(
    itemId,
    [
      'transcriptionEnabled',
      'languageCode',
      'transcriptionService',
      'labelDetectionEnabled',
      'textDetectionEnabled',
      'personDetectionEnabled',
      'createUnknownPersons',
      'transcriptionChannels',
      'transcriptionChannelIdentification',
      'textDetectionSettings',
      'celebrityDetectionEnabled',
      'analyticPlugins',
    ]
  );
  return analyticData;
};

type AnalyticsPropInItem = Pick<LoadedItem,
    'personDetectionEnabled'
  | 'celebrityDetectionEnabled'
  | 'itemType'
  | 'languageCode'
  | 'transcriptionService'
  | 'transcriptionChannels'
  | 'transcriptionChannelIdentification'
  | 'createUnknownPersons'
  | 'textDetectionSettings'
  | 'transcriptionEnabled'
  | 'labelDetectionEnabled'
  | 'textDetectionEnabled'
  | 'analyticPlugins'>;

export const getAnalyticConfigFromItem = (item: AnalyticsPropInItem, defaultConfig?: AnalyticConfig | Partial<AnalyticConfig>): AnalyticConfig => {
  const transcriptionConfig: ItemTranscriptionSettings | undefined =  getTranscriptionConfigFromItem(item) ?? defaultConfig?.transcriptionConfig;

  let personDetectionConfig: PersonDetectionParams | undefined;
  if (item.createUnknownPersons !== undefined) personDetectionConfig = { createUnknownPersons : item.createUnknownPersons };
  else if (defaultConfig?.personDetectionConfig !== undefined) personDetectionConfig = defaultConfig?.personDetectionConfig;
  else personDetectionConfig = { createUnknownPersons : false };

  let textDetectionSettings: TextDetectionSettings | undefined;
  if (item.textDetectionSettings) textDetectionSettings = item.textDetectionSettings;
  else textDetectionSettings = defaultConfig?.textDetectionSettings;


  const config : AnalyticConfig = {
    transcriptionEnabled: item.transcriptionEnabled ?? defaultConfig?.transcriptionEnabled ?? false,
    labelDetectionEnabled: item.labelDetectionEnabled ?? defaultConfig?.labelDetectionEnabled ?? false,
    textDetectionEnabled: item.textDetectionEnabled ?? defaultConfig?.textDetectionEnabled ?? false,
    personDetectionEnabled: item.personDetectionEnabled ?? defaultConfig?.personDetectionEnabled ?? false,
    celebrityDetectionEnabled: item.celebrityDetectionEnabled ?? defaultConfig?.celebrityDetectionEnabled ?? false,
    // Only relevant when transcription is enabled
    transcriptionConfig:  transcriptionConfig,
    // Only relevant when person detection is enabled
    personDetectionConfig: personDetectionConfig,
    // Only relevant when text detection is enabled
    textDetectionSettings: textDetectionSettings,
    analyticPlugins: item.analyticPlugins ?? defaultConfig?.analyticPlugins,
  };

  switch (item.itemType) {
    case ItemType.AUDIO:
      return getAnalyticConfig(ItemType.AUDIO, config);
    case ItemType.IMAGE:
      return getAnalyticConfig(ItemType.IMAGE, config);
    default:
      return config;
  }
};

export const getTranscriptionConfigFromItem = (item: Pick<LoadedItem, 'languageCode'| 'transcriptionService' | 'transcriptionChannels' | 'transcriptionChannelIdentification' >) => {
  const transcriptionConfig: ItemTranscriptionSettings = {
    transcriptionSourceLanguage: item.languageCode,
    transcriptionService: item.transcriptionService as TranscriptionService,
    transcriptionChannels: item.transcriptionChannels ?? undefined,
    transcriptionChannelIdentification: item.transcriptionChannelIdentification ?? undefined,
  };
  if (!transcriptionConfig.transcriptionService) return undefined;
  return transcriptionConfig;
};

export const normalizeAnalyticConfigForItem = (config: AnalyticConfig | Partial<AnalyticConfig>): CompleteType<ItemAnalyticProperties> => {
  return {
    transcriptionEnabled: config.transcriptionEnabled ?? false,
    languageCode: config.transcriptionConfig?.transcriptionSourceLanguage,
    transcriptionService: config.transcriptionConfig?.transcriptionService,
    transcriptionChannels: config.transcriptionConfig?.transcriptionChannels,
    transcriptionChannelIdentification: config.transcriptionConfig?.transcriptionChannelIdentification,
    labelDetectionEnabled: config.labelDetectionEnabled ?? false,
    textDetectionEnabled: config.textDetectionEnabled ?? false,
    textDetectionSettings: config.textDetectionSettings,
    personDetectionEnabled: config.personDetectionEnabled ?? false,
    createUnknownPersons: config.personDetectionConfig?.createUnknownPersons,
    celebrityDetectionEnabled: config.celebrityDetectionEnabled ?? false,
    analyticPlugins: config.analyticPlugins,
  };
};

/**
 * fetches AnalyticsPropInItem from server using id and creates @AnalyticConfig from props for the given type.
 *
 * returns @AnalyticConfig
 */
export const getAnalyticConfigFromItemIdAndType = async (itemId: string, itemType: DynamoItemType, defaultConfig?: AnalyticConfig) => {
  const analyticsDataOfItem = await getAnalyticConfigForItem(itemId);
  const analyticConfig = getAnalyticConfigFromItem({ ...analyticsDataOfItem, itemType }, defaultConfig) as AnalyticConfig;
  return analyticConfig;
};

export const uploadFileFromDropEvent = async (items: DataTransferItemList | undefined, uploadRequest: Omit<ItemUploadRequestPayload, 'files'>, singleItemUpload: boolean, hasFolderItems?: boolean) => {
  if (!items || items.length === 0) {
    showToast('No files found to upload', TOAST.WARNING);
    return;
  }
  const files: File[] = [];

  const hasFolders = hasFolderItems ?? checkIfItHasFolders(items);
  if (singleItemUpload) {
    let file = items[0].getAsFile();
    if (hasFolders) {
      showToast('Cannot upload a folder to a placeholder', TOAST.WARNING);
    } else if (items.length > 1 && file !== null || (items.length > 2 && file === null)) {
      showToast('Cannot upload multiple files to a placeholder', TOAST.WARNING);
    } else if (file === null) {
      // Work around a bug in Premiere 15.4 on MacOS where the datatransfer list contains
      // 2 items, where the first item is a null value. See #5192
      if (items.length === 2) {
        const potentialProperFile = items[1].getAsFile();
        if (potentialProperFile !== null) {
          file = potentialProperFile;
        }
      }
    }
    if (!file) {
      showToast('No files found to upload', TOAST.WARNING);
      return;
    }
    files.push(file);
  } else if (hasFolders) {
    const req: FolderUploadDialogRequest = {
      items: await createSummaryTree(items),
      targetFolderId: uploadRequest.targetFolderId ?? undefined,
      initialMetadataObj: uploadRequest.initialMetadataObj,
      relatedItemIds: uploadRequest.relatedItemIds,
      createdCallbacks: uploadRequest.createdCallbacks,
    };
    showFolderUploadDialog(req);
    return;
  } else {
    for (let i = 0; i < items.length; i++) {
      const file = items[i].getAsFile();
      if (file) files.push(file);
    }

  }

  if (files.length > 0) {
    if (files.length === 1 && uploadRequest.initialMetadataObj?.metadata) {
      // If it's a single file, then the file's original name should be set as the default title
      uploadRequest.initialMetadataObj.metadata.formData[SystemDefaultField.Title] = files[0].name;
    }

    showItemUploadDialog({ files: files.map(ensurePathForLocalCopy), ...uploadRequest });
  }
};

export const toBase64 = (file: File) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => resolve(reader.result);
  reader.onerror = error => { reader.abort(); reject(error); };
});

const notVisibleFilter = (layoutSettings: LayoutSettings[] | undefined) => {
  return layoutSettings ? layoutSettings.filter(field => !field.visible) : [];
};

export const getVisibleMetadataFromViewType = async (metadata: ItemMetaData, view: ViewType) => {
  await Promise.all([
    mdfState.ops.getOrLoadMdf(DEFAULT_MDF_ID),
    metadata.formId !== DEFAULT_MDF_ID ? mdfState.ops.getOrLoadMdf(metadata.formId) : Promise.resolve(undefined)
  ]);

  const allSchemas = mdfState.state.mdfs;
  const usedSchemas = getMainAndSubtypeSchemas(allSchemas, metadata.formId);
  const allFieldsLayoutSettings = usedSchemas.map(s => s.views?.[view]).flat();

  const notVisibleFieldsFromView = [...notVisibleFilter(allFieldsLayoutSettings)];
  const allFields = usedSchemas.map(s => s.fields).flat();

  for (const field of notVisibleFieldsFromView) {
    // It is possible that layoutSettings does not have id property, in that case we need to find the id from fields.
    const id = field.id ?? allFields.find(f => f.fieldId === field.fieldId)?.id;
    if (!id || id === SystemDefaultField.Title) continue;
    if (metadata.formData[id]) delete metadata.formData[id];
  }

  return metadata;
};


function collectFileKeysForDeduplication_(droppedEntity: DroppedEntity, res: ItemDeduplicationKey[]) {
  if (droppedEntity.type === 'file') {
    const d = droppedEntity as DroppedFile;
    res.push({
      name: d.name,
      size: d.size,
    });
    return;
  }

  const folder = droppedEntity as DroppedFolder;
  for (const subItem of folder.subItems) {
    collectFileKeysForDeduplication_(subItem, res);
  }
}


export function collectFileKeysForDeduplication(dropped: DroppedEntity): ItemDeduplicationKey[] {
  const res: ItemDeduplicationKey[] = [];
  collectFileKeysForDeduplication_(dropped, res);
  return res;
}


async function getFile(entry: WebKitEntry) {
  return new Promise<File>((resolve, reject) => {
    entry.file(file => { resolve(file); }, error => { reject(error); });
  });
}
